import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Disc3 } from "lucide-react";
import { mediaUrl } from "@/lib/api";

/**
 * Compact monthly grid highlighting upcoming release dates.
 * `upcoming` shape: [{ release_id, title, release_date (YYYY-MM-DD), cover_url, type, status, display_artist }]
 */
export default function ReleaseCalendar({ upcoming = [], goto }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() }; // month is 0-indexed
  });

  const byDate = useMemo(() => {
    const map = new Map();
    for (const r of upcoming) {
      const d = (r.release_date || "").slice(0, 10);
      if (!d) continue;
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(r);
    }
    return map;
  }, [upcoming]);

  const monthMeta = useMemo(() => {
    const first = new Date(Date.UTC(cursor.year, cursor.month, 1));
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
    // Convert Sunday=0 → Monday-first week (0..6 where 0 = Mon)
    const startDow = (first.getUTCDay() + 6) % 7;
    const label = first.toLocaleString("en", { month: "long", year: "numeric" });
    return { startDow, daysInMonth, label };
  }, [cursor]);

  const step = (delta) => {
    setCursor((c) => {
      const t = new Date(c.year, c.month + delta, 1);
      return { year: t.getFullYear(), month: t.getMonth() };
    });
  };

  const cells = [];
  for (let i = 0; i < monthMeta.startDow; i++) cells.push({ empty: true, key: `pad-${i}` });
  for (let d = 1; d <= monthMeta.daysInMonth; d++) {
    const iso = `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ empty: false, day: d, iso, releases: byDate.get(iso) || [], key: iso });
  }

  const todayIso = new Date().toISOString().slice(0, 10);

  const scheduledThisMonth = cells.filter((c) => !c.empty && c.releases.length > 0);
  const totalThisMonth = scheduledThisMonth.reduce((n, c) => n + c.releases.length, 0);

  const upcomingList = upcoming
    .filter((r) => (r.release_date || "").slice(0, 7) === `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}`)
    .sort((a, b) => a.release_date.localeCompare(b.release_date))
    .slice(0, 4);

  return (
    <div
      data-testid="release-calendar"
      className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 md:p-6 flex flex-col"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">Release calendar</div>
          <h3 className="mt-1 font-display text-xl text-white">{monthMeta.label}</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid="calendar-prev"
            onClick={() => step(-1)}
            className="h-8 w-8 rounded-full border border-white/10 flex items-center justify-center text-slate-300 hover:bg-white/5"
            aria-label="Previous month"
          >
            <ChevronLeft size={15} />
          </button>
          <button
            type="button"
            data-testid="calendar-next"
            onClick={() => step(1)}
            className="h-8 w-8 rounded-full border border-white/10 flex items-center justify-center text-slate-300 hover:bg-white/5"
            aria-label="Next month"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-1.5 text-center text-[10px] tracking-[0.15em] uppercase font-medium text-slate-500">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="mt-2 grid grid-cols-7 gap-1.5">
        {cells.map((c) =>
          c.empty ? (
            <div key={c.key} className="aspect-square" />
          ) : (
            <div
              key={c.key}
              data-testid={`cal-day-${c.iso}${c.releases.length ? "-has" : ""}`}
              className={`aspect-square rounded-lg border relative flex items-center justify-center text-sm ${
                c.iso === todayIso
                  ? "border-blue-500/50 bg-blue-500/10 text-white"
                  : c.releases.length
                  ? "border-blue-500/25 bg-blue-500/[0.04] text-white hover:bg-blue-500/10 cursor-pointer"
                  : "border-transparent text-slate-500"
              }`}
              title={c.releases.length ? c.releases.map((r) => r.title).join("\n") : ""}
              onClick={() => { if (c.releases.length && typeof goto === "function") goto("releases"); }}
            >
              <span>{c.day}</span>
              {c.releases.length > 0 && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {c.releases.slice(0, 3).map((_, i) => (
                    <span key={i} className="h-1 w-1 rounded-full bg-blue-400" />
                  ))}
                </span>
              )}
            </div>
          )
        )}
      </div>

      <div className="mt-5 pt-5 border-t border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] tracking-[0.15em] uppercase font-bold text-slate-500">
            {totalThisMonth === 0 ? "Nothing scheduled" : `${totalThisMonth} scheduled this month`}
          </div>
          {upcoming.length > 0 && (
            <button
              type="button"
              onClick={() => typeof goto === "function" && goto("releases")}
              className="text-[11px] text-blue-400 hover:text-blue-300"
              data-testid="calendar-view-all"
            >
              View all
            </button>
          )}
        </div>
        {upcomingList.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 py-6 px-4 text-center text-xs text-slate-500">
            No releases scheduled for this month.
          </div>
        ) : (
          <ul className="space-y-2">
            {upcomingList.map((r) => (
              <li
                key={r.release_id}
                data-testid={`calendar-item-${r.release_id}`}
                className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 hover:bg-white/[0.04] cursor-pointer"
                onClick={() => typeof goto === "function" && goto("releases")}
              >
                {r.cover_url ? (
                  <img src={mediaUrl(r.cover_url)} alt="" className="h-9 w-9 rounded-md object-cover" />
                ) : (
                  <div className="h-9 w-9 rounded-md bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                    <Disc3 size={14} className="text-blue-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{r.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">{r.type}{r.display_artist ? ` · ${r.display_artist}` : ""}</div>
                </div>
                <div className="text-[11px] text-slate-400 shrink-0 tabular-nums">
                  {new Date(r.release_date + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric" })}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
